import { useTranslation } from 'react-i18next';
import { useMemo } from 'react';

/**
 * Enterprise-grade hook for Reports Page translations with type safety and error handling
 * Provides centralized access to all Reports-related translations with memoization
 */
export function useReportspageTranslations() {
  // Use the default translation namespace since reportspage.json is merged into translation
  const { t } = useTranslation('translation');

  // Helper functions for common translation patterns with type safety
  const getMainText = (key: string, params?: Record<string, any>): string => {
    return String(t(`reportspage.main.${key}`, params || {}));
  };

  const getCompletenessText = (key: string, params?: Record<string, any>): string => {
    return String(t(`reportspage.completeness.${key}`, params || {}));
  };

  const getReadinessText = (key: string, params?: Record<string, any>): string => {
    return String(t(`reportspage.readiness.${key}`, params || {}));
  };

  const getVelocityText = (key: string, params?: Record<string, any>): string => {
    return String(t(`reportspage.velocity.${key}`, params || {}));
  };

  const getLocalizationText = (key: string, params?: Record<string, any>): string => {
    return String(t(`reportspage.localization.${key}`, params || {}));
  };

  const getHistoryText = (key: string, params?: Record<string, any>): string => {
    return String(t(`reportspage.history.${key}`, params || {}));
  };

  const getAttributesText = (key: string, params?: Record<string, any>): string => {
    return String(t(`reportspage.attributes.${key}`, params || {}));
  };

  const getFiltersText = (key: string, params?: Record<string, any>): string => {
    return String(t(`reportspage.filters.${key}`, params || {}));
  };

  const getExportText = (key: string, params?: Record<string, any>): string => {
    return String(t(`reportspage.export.${key}`, params || {}));
  };

  const getLoadingText = (key: string, params?: Record<string, any>): string => {
    return String(t(`reportspage.loading.${key}`, params || {}));
  };

  const getCommonText = (key: string, params?: Record<string, any>): string => {
    return String(t(`reportspage.common.${key}`, params || {}));
  };

  // Specific helpers for complex patterns with memoization
  const getSummaryCardText = useMemo(
    () => ({
      completeness: {
        overallCompleteness: getCompletenessText('summaryCards.overallCompleteness'),
        completedAttributes: getCompletenessText('summaryCards.completedAttributes'),
        missingAttributes: getCompletenessText('summaryCards.missingAttributes'),
        completionRate: getCompletenessText('summaryCards.completionRate'),
        missing: getCompletenessText('summaryCards.missing'),
        attributes: getCompletenessText('summaryCards.attributes'),
        ofAttributes: (total: number) =>
          getCompletenessText('summaryCards.ofAttributes', {
            total: total.toLocaleString(),
          }),
      },
      readiness: {
        overallReadiness: getReadinessText('summaryCards.overallReadiness'),
        totalAttributes: getReadinessText('summaryCards.totalAttributes'),
        completed: getReadinessText('summaryCards.completed'),
        missing: getReadinessText('summaryCards.missing'),
      },
      velocity: {
        totalEdits: getVelocityText('summaryCards.totalEdits'),
        avgTimeToPublish: getVelocityText('summaryCards.avgTimeToPublish'),
        productsEnriched: getVelocityText('summaryCards.productsEnriched'),
        activeEditors: getVelocityText('summaryCards.activeEditors'),
      },
    }),
    [t]
  );

  // Get translated tab names for reports
  const getTabsText = useMemo(
    () => ({
      dataCompleteness: String(t('reportspage.tabs.dataCompleteness')),
      localizationCoverage: String(t('reportspage.tabs.localizationCoverage')),
      productReadiness: String(t('reportspage.tabs.productReadiness')),
      enrichmentVelocity: String(t('reportspage.tabs.enrichmentVelocity')),
      attributeInsights: String(t('reportspage.tabs.attributeInsights')),
      changeHistory: String(t('reportspage.tabs.changeHistory')),
    }),
    [t]
  );

  const getChartText = useMemo(
    () => ({
      completeness: {
        completenessByAttribute: getCompletenessText('charts.completenessByAttribute'),
        completenessByAttributeDescription: getCompletenessText(
          'charts.completenessByAttributeDescription'
        ),
        completenessByCategory: getCompletenessText('charts.completenessByCategory'),
        completionRate: getCompletenessText('charts.completionRate'),
        completeness: getCompletenessText('charts.completeness'),
        complete: getCompletenessText('charts.complete'),
        moreAttributesNotShown: (count: number) =>
          getCompletenessText('charts.moreAttributesNotShown', { count }),
      },
      readiness: {
        completionOverview: getReadinessText('charts.completionOverview'),
        channelReadiness: getReadinessText('charts.channelReadiness'),
        fieldBreakdown: getReadinessText('charts.fieldBreakdown'),
        readiness: getReadinessText('charts.readiness'),
        attributes: getReadinessText('charts.attributes'),
      },
      velocity: {
        enrichmentVelocityTrend: getVelocityText('charts.enrichmentVelocityTrend'),
        topEditorsBreakdown: getVelocityText('charts.topEditorsBreakdown'),
        productsEnrichedOverTime: getVelocityText('charts.productsEnrichedOverTime'),
        editorPerformance: getVelocityText('charts.editorPerformance'),
        edits: getVelocityText('charts.edits'),
        avgTimeToPublish: getVelocityText('charts.avgTimeToPublish'),
        editor: getVelocityText('charts.editor'),
        editCount: getVelocityText('charts.editCount'),
      },
    }),
    [t]
  );

  const getErrorText = useMemo(
    () => ({
      completeness: {
        failedToLoad: getCompletenessText('errors.failedToLoad'),
        noDataAvailable: getCompletenessText('errors.noDataAvailable'),
        noAttributeData: getCompletenessText('errors.noAttributeData'),
        noCategoryData: getCompletenessText('errors.noCategoryData'),
        unexpectedError: getCompletenessText('errors.unexpectedError'),
        contactAdmin: getCompletenessText('errors.contactAdmin'),
      },
      readiness: {
        failedToLoad: (error: string) => getReadinessText('errors.failedToLoad', { error }),
        unknownError: getReadinessText('errors.unknownError'),
        noDataAvailable: getReadinessText('errors.noDataAvailable'),
        noChannelData: getReadinessText('errors.noChannelData'),
        channel: (channel: string) => getReadinessText('errors.channel', { channel }),
      },
      velocity: {
        failedToLoad: (error: string) => getVelocityText('errors.failedToLoad', { error }),
        unknownError: getVelocityText('errors.unknownError'),
        noDataAvailable: getVelocityText('errors.noDataAvailable'),
        noTrendData: getVelocityText('errors.noTrendData'),
        noEditorsData: getVelocityText('errors.noEditorsData'),
      },
      localization: {
        failedToLoad: getLocalizationText('errors.failedToLoad'),
        noDataAvailable: getLocalizationText('errors.noDataAvailable'),
      },
      history: {
        failedToLoad: getHistoryText('errors.failedToLoad'),
        noDataAvailable: getHistoryText('errors.noDataAvailable'),
      },
      attributes: {
        failedToLoad: getAttributesText('errors.failedToLoad'),
        noDataAvailable: getAttributesText('errors.noDataAvailable'),
      },
      filters: {
        loadingLocales: getFiltersText('errors.loadingLocales'),
        loadingChannels: getFiltersText('errors.loadingChannels'),
        loadingFamilies: getFiltersText('errors.loadingFamilies'),
        loadingCategories: getFiltersText('errors.loadingCategories'),
      },
    }),
    [t]
  );

  const getButtonText = useMemo(
    () => ({
      completeness: {
        showLess: getCompletenessText('buttons.showLess'),
        showAll: (count: number) => getCompletenessText('buttons.showAll', { count }),
      },
      filters: {
        reset: getFiltersText('reset'),
      },
      export: {
        exportCsv: getExportText('buttons.exportCsv'),
        exportExcel: getExportText('buttons.exportExcel'),
      },
    }),
    [t]
  );

  const getFiltersLabelsText = useMemo(
    () => ({
      title: getFiltersText('title'),
      labels: {
        locale: getFiltersText('labels.locale'),
        channel: getFiltersText('labels.channel'),
        family: getFiltersText('labels.family'),
        category: getFiltersText('labels.category'),
        dateRange: getFiltersText('labels.dateRange'),
      },
      placeholders: {
        allLocales: getFiltersText('placeholders.allLocales'),
        allChannels: getFiltersText('placeholders.allChannels'),
        allFamilies: getFiltersText('placeholders.allFamilies'),
        allCategories: getFiltersText('placeholders.allCategories'),
      },
      options: {
        all: getFiltersText('options.all'),
        noLocales: getFiltersText('options.noLocales'),
        noChannels: getFiltersText('options.noChannels'),
        noFamilies: getFiltersText('options.noFamilies'),
        noCategories: getFiltersText('options.noCategories'),
      },
    }),
    [t]
  );

  // Velocity time formatting helper
  const formatTimeText = useMemo(
    () => ({
      minutes: (time: number) => getVelocityText('timeFormats.minutes', { time: Math.round(time) }),
      hours: (time: number) => getVelocityText('timeFormats.hours', { time: time.toFixed(1) }),
      days: (time: number) => getVelocityText('timeFormats.days', { time: Math.round(time) }),
    }),
    [t]
  );

  // Safe translation wrapper that always returns string
  const tSafe = (key: string, params?: Record<string, any>): string => {
    return String(t(key, params || {}));
  };

  // Tooltip formatter for attribute details
  const getAttributeTooltip = (name: string, completed: number, total: number): string => {
    return getCompletenessText('tooltipLabels.attributeTooltip', {
      name,
      completed,
      total,
    });
  };

  // Chart data labels with translation
  const getChartDataLabels = useMemo(
    () => ({
      readiness: {
        completed: getReadinessText('chartData.completed'),
        missing: getReadinessText('chartData.missing'),
      },
    }),
    [t]
  );

  // Export messages
  const getExportMessages = useMemo(
    () => ({
      exportInitiated: getExportText('messages.exportInitiated'),
      exportFailed: getExportText('messages.exportFailed'),
      unknownReportType: (type: string) => getExportText('errors.unknownReportType', { type }),
    }),
    [t]
  );

  // Main page text
  const getMainPageText = useMemo(
    () => ({
      title: getMainText('title'),
      subtitle: getMainText('subtitle'),
      loading: getMainText('loading'),
      errorLoading: getMainText('errorLoading'),
      reportNotFound: getMainText('reportNotFound'),
      reportNotImplemented: getMainText('reportNotImplemented'),
      noThemesConfigured: getMainText('noThemesConfigured'),
      noThemesAvailable: getMainText('noThemesAvailable'),
    }),
    [t]
  );

  // Status labels
  const getStatusLabels = useMemo(
    () => ({
      unknown: getCompletenessText('statusLabels.unknown'),
      uncategorized: getCompletenessText('statusLabels.uncategorized'),
      unknownCategory: getCompletenessText('statusLabels.unknownCategory'),
    }),
    [t]
  );

  // Velocity table text helper
  const getVelocityTableText = useMemo(
    () => ({
      headers: {
        rank: getVelocityText('table.headers.rank'),
        editor: getVelocityText('table.headers.editor'),
        editCount: getVelocityText('table.headers.editCount'),
        performance: getVelocityText('table.headers.performance'),
      },
      performance: {
        high: getVelocityText('table.performance.high'),
        medium: getVelocityText('table.performance.medium'),
        low: getVelocityText('table.performance.low'),
      },
    }),
    [t]
  );

  // Change History text helpers
  const getHistoryFiltersText = useMemo(
    () => ({
      title: getHistoryText('filters.title'),
      dateRange: getHistoryText('filters.dateRange'),
      user: getHistoryText('filters.user'),
      entity: getHistoryText('filters.entity'),
      action: getHistoryText('filters.action'),
      reset: getHistoryText('filters.reset'),
      clearAll: getHistoryText('filters.clearAll'),
      searchEntity: getHistoryText('filters.searchEntity'),
      noUsersAvailable: getHistoryText('filters.noUsersAvailable'),
    }),
    [t]
  );

  const getHistoryTableText = useMemo(
    () => ({
      headers: {
        date: getHistoryText('table.headers.date'),
        user: getHistoryText('table.headers.user'),
        entity: getHistoryText('table.headers.entity'),
        action: getHistoryText('table.headers.action'),
        details: getHistoryText('table.headers.details'),
        changes: getHistoryText('table.headers.changes'),
      },
    }),
    [t]
  );

  const getHistoryActionsText = useMemo(
    () => ({
      created: getHistoryText('actions.created'),
      updated: getHistoryText('actions.updated'),
      deleted: getHistoryText('actions.deleted'),
      archived: getHistoryText('actions.archived'),
      create: getHistoryText('actions.create'),
      update: getHistoryText('actions.update'),
      delete: getHistoryText('actions.delete'),
      archive: getHistoryText('actions.archive'),
    }),
    [t]
  );

  const getHistoryActionLabelsText = useMemo(
    () => ({
      create: getHistoryText('actionLabels.create'),
      update: getHistoryText('actionLabels.update'),
      delete: getHistoryText('actionLabels.delete'),
      archive: getHistoryText('actionLabels.archive'),
      created: getHistoryText('actionLabels.created'),
      updated: getHistoryText('actionLabels.updated'),
      deleted: getHistoryText('actionLabels.deleted'),
      archived: getHistoryText('actionLabels.archived'),
    }),
    [t]
  );

  const getHistoryDetailedChangesText = useMemo(
    () => ({
      noChangesAvailable: getHistoryText('detailedChanges.noChangesAvailable'),
      noChangesDetected: getHistoryText('detailedChanges.noChangesDetected'),
      added: getHistoryText('detailedChanges.added'),
      removed: getHistoryText('detailedChanges.removed'),
      changedFrom: getHistoryText('detailedChanges.changedFrom'),
      to: getHistoryText('detailedChanges.to'),
      setTo: getHistoryText('detailedChanges.setTo'),
      wasValue: (value: string) => getHistoryText('detailedChanges.wasValue', { value }),
      addedValue: getHistoryText('detailedChanges.addedValue'),
      removedValue: getHistoryText('detailedChanges.removedValue'),
      changedFromTo: getHistoryText('detailedChanges.changedFromTo'),
    }),
    [t]
  );

  const getHistoryFilterChipsText = useMemo(
    () => ({
      from: getHistoryText('filterChips.from'),
      to: getHistoryText('filterChips.to'),
      user: getHistoryText('filterChips.user'),
      entity: getHistoryText('filterChips.entity'),
      action: getHistoryText('filterChips.action'),
    }),
    [t]
  );

  // Attributes text helpers
  const getAttributesChartsText = useMemo(
    () => ({
      attributeAdoptionRates: getAttributesText('charts.attributeAdoptionRates'),
      adoptionRatesDescription: getAttributesText('charts.adoptionRatesDescription'),
      adoptionDetails: getAttributesText('charts.adoptionDetails'),
      adoptionRate: getAttributesText('charts.adoptionRate'),
    }),
    [t]
  );

  const getAttributesTableText = useMemo(
    () => ({
      headers: {
        attribute: getAttributesText('table.headers.attribute'),
        type: getAttributesText('table.headers.type'),
        adoption: getAttributesText('table.headers.adoption'),
        filled: getAttributesText('table.headers.filled'),
        trend: getAttributesText('table.headers.trend'),
        uniqueValues: getAttributesText('table.headers.uniqueValues'),
      },
    }),
    [t]
  );

  const getAttributesSummaryCardsText = useMemo(
    () => ({
      avgAdoption: getAttributesText('summaryCards.avgAdoption'),
      totalAttributes: getAttributesText('summaryCards.totalAttributes'),
      trendingUp: getAttributesText('summaryCards.trendingUp'),
    }),
    [t]
  );

  const getAttributesBadgesText = useMemo(
    () => ({
      lastUpdated: getAttributesText('badges.lastUpdated'),
      unknown: getAttributesText('badges.unknown'),
    }),
    [t]
  );

  // Card components helpers
  const getGlobalCoverageCardText = useMemo(
    () => ({
      title: String(t('reportspage.cards.globalCoverage.title')),
      subtitle: String(t('reportspage.cards.globalCoverage.subtitle')),
      tooltip: String(t('reportspage.cards.globalCoverage.tooltip')),
      dataCompleteness: String(t('reportspage.cards.globalCoverage.dataCompleteness')),
      completedAttributes: String(t('reportspage.cards.globalCoverage.completedAttributes')),
      missingAttributes: String(t('reportspage.cards.globalCoverage.missingAttributes')),
      totalAttributes: String(t('reportspage.cards.globalCoverage.totalAttributes')),
      errors: {
        title: String(t('reportspage.cards.globalCoverage.errors.title')),
        failedToLoad: String(t('reportspage.cards.globalCoverage.errors.failedToLoad')),
        retry: String(t('reportspage.cards.globalCoverage.errors.retry')),
      },
    }),
    [t]
  );

  const getMissingAttributesHeatmapCardText = useMemo(
    () => ({
      title: String(t('reportspage.cards.missingAttributesHeatmap.title')),
      subtitle: String(t('reportspage.cards.missingAttributesHeatmap.subtitle')),
      errors: {
        errorLoadingData: String(
          t('reportspage.cards.missingAttributesHeatmap.errors.errorLoadingData')
        ),
        problemFetching: String(
          t('reportspage.cards.missingAttributesHeatmap.errors.problemFetching')
        ),
        retry: String(t('reportspage.cards.missingAttributesHeatmap.errors.retry')),
      },
      noData: {
        noDataAvailable: String(
          t('reportspage.cards.missingAttributesHeatmap.noData.noDataAvailable')
        ),
        tryAdjusting: String(t('reportspage.cards.missingAttributesHeatmap.noData.tryAdjusting')),
      },
      heatmap: {
        family: String(t('reportspage.cards.missingAttributesHeatmap.heatmap.family')),
        fewer: String(t('reportspage.cards.missingAttributesHeatmap.heatmap.fewer')),
        more: String(t('reportspage.cards.missingAttributesHeatmap.heatmap.more')),
        unknown: String(t('reportspage.cards.missingAttributesHeatmap.heatmap.unknown')),
      },
    }),
    [t]
  );

  const getLocalizationCoverageCardText = useMemo(
    () => ({
      title: String(t('reportspage.cards.localizationCoverage.title')),
      subtitle: String(t('reportspage.cards.localizationCoverage.subtitle')),
      localesCount: (count: number) =>
        String(t('reportspage.cards.localizationCoverage.localesCount', { count })),
      averageCompletion: String(t('reportspage.cards.localizationCoverage.averageCompletion')),
      excellentLocales: String(t('reportspage.cards.localizationCoverage.excellentLocales')),
      localesByCompletion: String(t('reportspage.cards.localizationCoverage.localesByCompletion')),
      localeDetails: (name: string) =>
        String(t('reportspage.cards.localizationCoverage.localeDetails', { name })),
      topLeastCompleted: String(t('reportspage.cards.localizationCoverage.topLeastCompleted')),
      attributes: String(t('reportspage.cards.localizationCoverage.attributes')),
      missing: String(t('reportspage.cards.localizationCoverage.missing')),
      needAttention: (count: number) =>
        String(
          t('reportspage.cards.localizationCoverage.needAttention', {
            count,
            plural: count !== 1 ? 's' : '',
          })
        ),
      allPerformingWell: String(t('reportspage.cards.localizationCoverage.allPerformingWell')),
      completionBadges: {
        excellent: String(t('reportspage.cards.localizationCoverage.completionBadges.excellent')),
        good: String(t('reportspage.cards.localizationCoverage.completionBadges.good')),
        fair: String(t('reportspage.cards.localizationCoverage.completionBadges.fair')),
        needsWork: String(t('reportspage.cards.localizationCoverage.completionBadges.needsWork')),
      },
      errors: {
        errorLoadingData: String(
          t('reportspage.cards.localizationCoverage.errors.errorLoadingData')
        ),
        problemFetching: String(t('reportspage.cards.localizationCoverage.errors.problemFetching')),
        retry: String(t('reportspage.cards.localizationCoverage.errors.retry')),
      },
      noData: {
        title: String(t('reportspage.cards.localizationCoverage.noData.title')),
        message: String(t('reportspage.cards.localizationCoverage.noData.message')),
        refreshData: String(t('reportspage.cards.localizationCoverage.noData.refreshData')),
      },
    }),
    [t]
  );

  return {
    // Direct translation function with type safety
    t: tSafe,

    // Category-specific helpers
    getMainText,
    getCompletenessText,
    getReadinessText,
    getVelocityText,
    getLocalizationText,
    getHistoryText,
    getAttributesText,
    getFiltersText,
    getExportText,
    getLoadingText,
    getCommonText,

    // Memoized complex helpers to prevent re-renders
    summaryCards: getSummaryCardText,
    charts: getChartText,
    errors: getErrorText,
    buttons: getButtonText,
    filters: getFiltersLabelsText,
    formatTime: formatTimeText,
    chartDataLabels: getChartDataLabels,
    exportMessages: getExportMessages,
    mainPage: getMainPageText,
    statusLabels: getStatusLabels,
    velocityTable: getVelocityTableText,

    // Change History helpers
    historyFilters: getHistoryFiltersText,
    historyTable: getHistoryTableText,
    historyActions: getHistoryActionsText,
    historyActionLabels: getHistoryActionLabelsText,
    historyDetailedChanges: getHistoryDetailedChangesText,
    historyFilterChips: getHistoryFilterChipsText,

    // Attributes helpers
    attributesCharts: getAttributesChartsText,
    attributesTable: getAttributesTableText,
    attributesSummaryCards: getAttributesSummaryCardsText,
    attributesBadges: getAttributesBadgesText,

    // Utility functions
    getAttributeTooltip,

    // Card components helpers
    getGlobalCoverageCardText,
    getMissingAttributesHeatmapCardText,
    getLocalizationCoverageCardText,

    // Get translated tab names for reports
    getTabsText,
  };
}

export type ReportspageTranslations = ReturnType<typeof useReportspageTranslations>;
